import { Graphics } from '@inlet/react-pixi';
import { Tag } from 'lib-shared-legacy-types';
import _groupBy from 'lodash/groupBy';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { useDrawTagCircle } from 'components/powerFlowShared/hooks/useDrawTagCircle';
import { flowNodeColor } from 'fable/flowEditor';
import { hexStringToNumber } from 'fable/flowEditor/components/flowManager/canvas/renderers/common/colors';
import { PhoneExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'fable/flowEditor/components/flowManager/canvas/types/node';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';

import TagsModal from '../../../containers/modals/tags';

export type Payload = {
  next_node_id: string;
  tag_ids: string[];
};

type TagsNodeAttributes = GenericNodeAttributes<Payload, PhoneExtraData>;
type TagsNodeProps = GenericNodeProps<Payload, PhoneExtraData>;

const translatePayload: TagsNodeAttributes['translatePayload'] = (node) => {
  const { next_node_id } = node.payload;

  return next_node_id
    ? [
        {
          id: uuid(),
          label: 'arrow',
          connection: next_node_id,
        },
      ]
    : [];
};

const updateConnection: TagsNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload: Payload = {
    ...node.payload,
    next_node_id: connectionId,
  };

  return { ...node, payload };
};

export const TagsNodeAttributes: TagsNodeAttributes = {
  iconName: 'tag',
  label: 'Tags',
  helpText:
    'Automatically apply custom tags to gain insights about your customer conversations (e.g. identify your VIP customers). ',
  color: hexStringToNumber(flowNodeColor.blue),
  translatePayload,
  updateConnection,
  NodeContent: TagsNode,
  ActionModal: TagsModal,
};

export function TagsNode({ node, context }: TagsNodeProps): JSX.Element {
  const { payload } = node;
  const { extraData } = context;
  const { tags } = extraData;
  const { tag_ids } = payload;

  const resolvedTags = useMemo(() => {
    if (tags.length) {
      return tags.filter(({ name }) => tag_ids.indexOf(name) > -1);
    }

    // In case we can't resolve the tags using the names from the API, we can fall back to showing
    // the name that we have in the node payload without color, instead of showing 'unknown tag'.
    return tag_ids.map(
      (name) =>
        ({
          id: name,
          name,
          type: 'conversation',
          usage_count: 0,
        } as Tag),
    );
  }, [tags, tag_ids]);

  const [firstTag] = resolvedTags;
  const drawTagCircle = useDrawTagCircle(firstTag, { x: 5, y: 12 });

  return (
    <>
      <Graphics draw={drawTagCircle} />
      <TextContent width={200} offset={{ x: 15, y: 0 }}>
        {useMemo(
          () => (
            <TextEllipsis>
              <Text weight="semibold" text={firstTag?.name || 'unknown tag'} />
            </TextEllipsis>
          ),
          [firstTag],
        )}
      </TextContent>
      <TextContent width={230} offset={{ x: 200, y: 0 }}>
        {useMemo(() => {
          if (resolvedTags.length < 2) {
            return <></>;
          }

          return (
            <TextEllipsis>
              <Text weight="normal" text={`+${resolvedTags.length - 1}`} />
            </TextEllipsis>
          );
        }, [resolvedTags.length])}
      </TextContent>
    </>
  );
}
